import React, { useEffect, useRef, useState } from 'react';
import { Cpu, X, Zap, Activity, GitBranch, Square, Star, Edit2, Trash2 } from 'lucide-react';
import * as swd from '../../../../../../assets/js/horizontal_designer';
import { AgentData } from '../../../../../../types/Agent.types';
import { useHistory, useLocation } from 'react-router-dom';
import Modal from '../../../../../../components/shared/Modal/Modal';
import { useToast } from '../../../../../../hooks/useToast';
import './AgentEditor.css';

export const AgentEditor: React.FC = () => {
  const { notify } = useToast();
  const history = useHistory();
  const location = useLocation<{ agent: AgentData }>();
  const agent = location.state?.agent;

  const placeholderRef = useRef<HTMLDivElement>(null);
  const designerRef = useRef<any>(null);

  const [blockToDelete, setBlockToDelete] = useState<{ id: string; label: string } | null>(null);

  const [savedBlocks, setSavedBlocks] = useState<any[]>(() => {
    try {
      const stored = localStorage.getItem('agent-editor-saved-blocks');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });
  const savedBlocksRef = useRef(savedBlocks);
  useEffect(() => {
    savedBlocksRef.current = savedBlocks;
  }, [savedBlocks]);

  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('agent-editor-favorites');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });

  const toggleFavorite = (id: string, e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setFavorites(prev => {
      const newFavs = prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id];
      localStorage.setItem('agent-editor-favorites', JSON.stringify(newFavs));
      return newFavs;
    });
  };

  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const [editingBlockName, setEditingBlockName] = useState<string>('');

  const handleEditBlockStart = (id: string, currentName: string, e: React.MouseEvent | React.TouchEvent | React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingBlockId(id);
    setEditingBlockName(currentName);
  };

  const handleEditBlockSave = (id: string, value: string, e?: React.SyntheticEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (value.trim()) {
      setSavedBlocks(prev => {
        const updated = prev.map(b => b.id === id ? { ...b, name: value } : b);
        localStorage.setItem('agent-editor-saved-blocks', JSON.stringify(updated));
        return updated;
      });
    }
    setEditingBlockId(prev => (prev === id ? null : prev));
  };

  const handleDeleteBlock = (id: string, label: string, e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setBlockToDelete({ id, label });
  };

  const confirmDeleteBlock = () => {
    if (!blockToDelete) return;
    const { id } = blockToDelete;
    setSavedBlocks(prev => {
      const updated = prev.filter(b => b.id !== id);
      localStorage.setItem('agent-editor-saved-blocks', JSON.stringify(updated));
      return updated;
    });
    setFavorites(prev => {
      if (prev.includes(id)) {
        const newFavs = prev.filter(f => f !== id);
        localStorage.setItem('agent-editor-favorites', JSON.stringify(newFavs));
        return newFavs;
      }
      return prev;
    });
    setBlockToDelete(null);
    notify('Éxito', 'Bloque eliminado exitosamente', 'info');
  };

  const handleMouseDown = (e: React.MouseEvent, stepDef: any) => {
    e.stopPropagation();
    if (e.button !== 0) return; // Only primary button
    if (!designerRef.current || !designerRef.current.api || !swd.Vector) return;

    const position = new swd.Vector(e.pageX, e.pageY);
    designerRef.current.api.toolbox.tryDrag(position, stepDef);
  };

  const handleTouchStart = (e: React.TouchEvent, stepDef: any) => {
    e.stopPropagation();
    if (!designerRef.current || !designerRef.current.api || !swd.Vector) return;

    const touch = e.touches[0];
    const position = new swd.Vector(touch.pageX, touch.pageY);
    designerRef.current.api.toolbox.tryDrag(position, stepDef);
  };

  const onClose = () => {
    history.push('/agents');
  };

  useEffect(() => {
    if (!placeholderRef.current || !agent) return;

    // Mock initial data using the user's labels and types mapped to sequential-workflow-designer format
    const definition = {
      properties: {},
      sequence: [
        { id: '1', componentType: 'task', type: 'custom', name: 'INICIO_PROCESO', properties: { type: 'TRIGGER' } },
        { id: '2', componentType: 'task', type: 'custom', name: 'ANALISIS_DATOS', properties: { type: 'ACTION' } },
        { id: '3', componentType: 'task', type: 'custom', name: 'GENERAR_REPORTE', properties: { type: 'ACTION' } },
        { id: '4', componentType: 'task', type: 'custom', name: 'FIN_PROCESO', properties: { type: 'END' } }
      ]
    };

    const isDark = document.documentElement.classList.contains('dark');

    const configuration = {
      theme: isDark ? 'dark' : 'light',
      isReadonly: false,
      undoStackSize: 10,
      controlBar: true,
      toolbox: false,
      steps: {
        canInsertStep: () => true,
        canMoveStep: () => true,
        canDeleteStep: () => true
      },
      iconUrlProvider: (componentType: string, type: string) => {
        if (type === 'closecross' || type === 'closecrossb') {
          return 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScyNCcgaGVpZ2h0PScyNCcgdmlld0JveD0nMCAwIDI0IDI0JyBmaWxsPSdub25lJyBzdHJva2U9JyNlZjQ0NDQnIHN0cm9rZS13aWR0aD0nMicgc3Ryb2tlLWxpbmVjYXA9J3JvdW5kJyBzdHJva2UtbGluZWpvaW49J3JvdW5kJz48cG9seWxpbmUgcG9pbnRzPSczIDYgNSA2IDIxIDYnPjwvcG9seWxpbmU+PHBhdGggZD0nTTE5IDZ2MTRhMiAyIDAgMCAxLTIgMkg3YTIgMiAwIDAgMS0yLTJWNm0zIDBWNGEyIDIgMCAwIDEgMi0yaDRhMiAyIDAgMCAxIDIgMnYyJz48L3BhdGg+PGxpbmUgeDE9JzEwJyB5MT0nMTEnIHgyPScxMCcgeTI9JzE3Jz48L2xpbmU+PGxpbmUgeDE9JzE0JyB5MT0nMTEnIHgyPScxNCcgeTI9JzE3Jz48L2xpbmU+PC9zdmc+';
        }
        return '';
      }
    };

    if (swd && swd.Designer) {
      designerRef.current = swd.Designer.create(placeholderRef.current, definition, configuration);
      (window as any)._zeroDesigner = designerRef.current;
    } else {
      console.error('sequentialWorkflowDesigner library not loaded');
    }

    (window as any).countEditorSelectedSteps = () => {
      return (window as any).editorSelectedStepIds ? (window as any).editorSelectedStepIds.length : 0;
    };

    (window as any).saveEditorSelectedBlock = () => {
      const ids = (window as any).editorSelectedStepIds || [];
      if (ids.length < 2) return;
      if (!designerRef.current) return;

      const currentDef = typeof designerRef.current.getDefinition === 'function'
        ? designerRef.current.getDefinition()
        : definition;

      if (!currentDef) return;

      const selectedSteps: any[] = [];
      const extractSteps = (sequence: any[]) => {
        if (!sequence) return;
        for (const s of sequence) {
          if (ids.includes(s.id)) {
            selectedSteps.push(JSON.parse(JSON.stringify(s)));
          }
          if (s.sequence) extractSteps(s.sequence);
          if (s.branches) {
            for (const branch of Object.values(s.branches)) {
              extractSteps(branch as any[]);
            }
          }
        }
      };

      extractSteps(currentDef.sequence);

      if (selectedSteps.length > 0) {
        const blockName = `Bloque ${savedBlocksRef.current.length + 1}`;

        const newBlock = {
          id: `block-${Date.now()}`,
          name: blockName,
          type: 'custom',
          componentType: 'task',
          __blockSteps: selectedSteps,
          properties: { type: 'BLOQUE', steps: selectedSteps }
        };

        const updatedBlocks = [...savedBlocksRef.current, newBlock];
        setSavedBlocks(updatedBlocks);
        localStorage.setItem('agent-editor-saved-blocks', JSON.stringify(updatedBlocks));

        document.querySelectorAll('.sqd-marquee-selected').forEach(el => el.classList.remove('sqd-marquee-selected'));
        (window as any).editorSelectedStepIds = [];
      }
    };

    const updateTheme = () => {
      const isNowDark = document.documentElement.classList.contains('dark');
      if (placeholderRef.current) {
        const designerEl = placeholderRef.current.querySelector('.sqd-designer');
        if (designerEl) {
          designerEl.classList.remove('sqd-theme-light', 'sqd-theme-dark');
          designerEl.classList.add(isNowDark ? 'sqd-theme-dark' : 'sqd-theme-light');
        }
      }
    };

    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    const domObserver = new MutationObserver(() => {
      if (!designerRef.current) return;

      const hosts = [document.body];
      try {
        if (window.frameElement && window.parent && window.parent.document) {
          hosts.push(window.parent.document.body);
        }
      } catch (e) { }

      const tasks: Element[] = [];
      hosts.forEach(host => {
        tasks.push(...Array.from(host.querySelectorAll('.sqd-step-task')));
      });

      if (tasks.length === 0) return;

      const currentDef = typeof designerRef.current.getDefinition === 'function'
        ? designerRef.current.getDefinition()
        : definition;

      const findStepById = (sequence: any[], id: string): any => {
        if (!sequence) return null;
        for (const s of sequence) {
          if (s.id === id) return s;
          if (s.sequence) {
            const found = findStepById(s.sequence, id);
            if (found) return found;
          }
          if (s.branches) {
            for (const branch of Object.values(s.branches)) {
              const found = findStepById(branch as any[], id);
              if (found) return found;
            }
          }
        }
        return null;
      };

      tasks.forEach(task => {
        if (task.querySelector('.custom-type-title')) return;

        const fillRect = task.querySelector('.fondo-fill');
        if (!fillRect) return;

        const idMatch = fillRect.id.match(/^fill-(.+)$/);
        if (!idMatch) return;

        const stepId = idMatch[1];
        const step = findStepById(currentDef.sequence, stepId);

        if (step && step.properties?.type) {
          const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');
          textEl.setAttribute('class', 'sqd-step-task-text-value custom-type-title');
          textEl.setAttribute('x', '15');
          textEl.setAttribute('y', '26');
          textEl.textContent = step.properties.type;
          task.appendChild(textEl);
        }

        const nameTextEl = task.querySelector('.sqd-step-task-text');
        if (nameTextEl) {
          nameTextEl.setAttribute('x', '15');
          nameTextEl.setAttribute('y', '50');
        }
      });
    });

    const hosts = [document.body];
    try {
      if (window.frameElement && window.parent && window.parent.document) {
        hosts.push(window.parent.document.body);
      }
    } catch (e) { }

    hosts.forEach(host => {
      domObserver.observe(host, { childList: true, subtree: true });
    });

    const handleViewportChange = (e: Event) => {
      if (!placeholderRef.current) return;
      const customEvent = e as CustomEvent;
      const { position, scale } = customEvent.detail;
      const scaledSize = 40 * scale;

      placeholderRef.current.style.backgroundSize = `${scaledSize}px ${scaledSize}px`;
      placeholderRef.current.style.backgroundPosition = `${position.x}px ${position.y}px`;
    };

    window.addEventListener('viewportChanged', handleViewportChange);

    return () => {
      observer.disconnect();
      domObserver.disconnect();
      window.removeEventListener('viewportChanged', handleViewportChange);
      if (designerRef.current) {
        designerRef.current.destroy();
      }
    };
  }, [agent]);

  if (!agent) {
    return <div className="agent-editor-container" style={{ padding: '2rem' }}>No agent selected. Please go back to the agents list.</div>;
  }

  const standardItems = [
    {
      id: 'trigger',
      icon: Zap,
      iconClass: 'agent-editor-floating-menu-item-icon-trigger',
      label: 'Trigger',
      stepDef: {
        componentType: 'task',
        type: 'custom',
        name: 'Trigger',
        properties: { type: 'TRIGGER' }
      }
    },
    {
      id: 'action',
      icon: Activity,
      iconClass: 'agent-editor-floating-menu-item-icon-action',
      label: 'Acción',
      stepDef: {
        componentType: 'task',
        type: 'custom',
        name: 'Acción',
        properties: { type: 'ACTION' }
      }
    },
    {
      id: 'condition',
      icon: GitBranch,
      iconClass: 'agent-editor-floating-menu-item-icon-condition',
      label: 'Condición',
      stepDef: {
        componentType: 'switch',
        type: 'custom',
        name: 'Condición',
        properties: { type: 'CONDITION' },
        branches: { 'True': [], 'False': [] }
      }
    },
    {
      id: 'end',
      icon: Square,
      iconClass: 'agent-editor-floating-menu-item-icon-end',
      label: 'Fin',
      stepDef: {
        componentType: 'task',
        type: 'custom',
        name: 'Fin',
        properties: { type: 'END' }
      }
    }
  ];

  const customBlockItems = savedBlocks.map(block => ({
    id: block.id,
    icon: Activity,
    iconClass: 'agent-editor-floating-menu-item-icon-action',
    label: block.name,
    stepDef: {
      ...block,
      __blockSteps: block.__blockSteps || block.properties?.steps || []
    },
    isCustomBlock: true
  }));

  const allItems = [
    ...standardItems,
    ...customBlockItems
  ];

  const favoriteItems = allItems.filter(item => favorites.includes(item.id));

  const renderMenuItem = (item: any, isFavSection: boolean = false) => {
    const isFav = favorites.includes(item.id);
    const isEditing = editingBlockId === item.id;
    const Icon = item.icon;
    return (
      <button
        key={isFavSection ? `fav-${item.id}` : item.id}
        className="agent-editor-floating-menu-item"
        style={{ position: 'relative', paddingRight: item.isCustomBlock ? (isEditing ? '3.5rem' : '5rem') : '2rem' }}
        onMouseDown={(e) => { if (!isEditing) handleMouseDown(e, item.stepDef); }}
        onTouchStart={(e) => { if (!isEditing) handleTouchStart(e, item.stepDef); }}
      >
        <Icon size={12} className={item.iconClass} />
        {isEditing ? (
          <input
            type="text"
            value={editingBlockName}
            onChange={(e) => setEditingBlockName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleEditBlockSave(item.id, e.currentTarget.value, e);
              if (e.key === 'Escape') { e.stopPropagation(); setEditingBlockId(null); }
            }}
            onBlur={(e) => handleEditBlockSave(item.id, e.target.value, e)}
            autoFocus
            style={{
              flex: 1,
              background: 'transparent',
              border: '1px solid var(--color-brand-green)',
              color: 'var(--color-brand-text-bright)',
              fontSize: '9px',
              fontFamily: 'inherit',
              textTransform: 'uppercase',
              outline: 'none',
              padding: '2px',
              width: 'calc(100% - 1rem)'
            }}
            onMouseDown={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <span className="agent-editor-floating-menu-item-text">{item.label}</span>
        )}

        <div
          style={{
            position: 'absolute',
            right: '0.5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            zIndex: 10
          }}
        >
          {item.isCustomBlock && (
            <>
              <div
                onMouseDown={(e) => {
                  if (isEditing) {
                    e.stopPropagation();
                    e.preventDefault();
                    setEditingBlockId(null);
                  } else {
                    handleEditBlockStart(item.id, item.label, e);
                  }
                }}
                onTouchStart={(e) => {
                  if (isEditing) {
                    e.stopPropagation();
                    e.preventDefault();
                    setEditingBlockId(null);
                  } else {
                    handleEditBlockStart(item.id, item.label, e);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '6px',
                  color: isEditing ? '#ef4444' : 'var(--color-brand-text, #666)',
                  cursor: 'pointer',
                }}
              >
                {isEditing ? <X size={12} /> : <Edit2 size={12} />}
              </div>
              {!isEditing && (
                <div
                  onMouseDown={(e) => handleDeleteBlock(item.id, item.label, e)}
                  onTouchStart={(e) => handleDeleteBlock(item.id, item.label, e)}
                  onClick={(e) => e.stopPropagation()}
                  className="agent-editor-floating-menu-item-delete"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '6px',
                    cursor: 'pointer',
                  }}
                >
                  <Trash2 size={12} />
                </div>
              )}
            </>
          )}
          <div
            className="agent-editor-floating-menu-item-star"
            onMouseDown={(e) => toggleFavorite(item.id, e)}
            onTouchStart={(e) => toggleFavorite(item.id, e)}
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px',
              color: isFav ? '#facc15' : 'var(--color-brand-text, #666)',
              opacity: isFav ? 1 : 0.4,
              cursor: 'pointer',
            }}
          >
            <Star size={12} fill={isFav ? 'currentColor' : 'none'} />
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="agent-editor-container">
      <div className="agent-editor-header">
        <div className="agent-editor-header-left">
          <div className="agent-editor-icon">
            <Cpu size={20} />
          </div>
          <div className="agent-editor-title-container">
            <h2 className="agent-editor-title">
              Editor_Flujo_Procesos <span className="agent-editor-title-separator">//</span> {agent.name ? agent.name.toUpperCase() : 'AGENTE'}
            </h2>
            <p className="agent-editor-subtitle">
              Diseño de lógica operativa y flujos de decisión para el agente.
            </p>
          </div>
        </div>
        <div className="agent-editor-header-right">
          <button className="agent-editor-save-btn">
            Guardar_Cambios
          </button>
          <button className="agent-editor-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
      </div>
      <div className="agent-editor-canvas" ref={placeholderRef}>
        <div className="agent-editor-floating-menu">
          {favoriteItems.length > 0 && (
            <>
              <div className="agent-editor-floating-menu-title">Favoritos</div>
              <div className="agent-editor-floating-menu-grid" style={{ marginBottom: '1rem' }}>
                {favoriteItems.map(item => renderMenuItem(item, true))}
              </div>
            </>
          )}
          <div className="agent-editor-floating-menu-title">Herramientas_Flujo</div>
          <div className="agent-editor-floating-menu-grid" style={customBlockItems.length > 0 ? { marginBottom: '1rem' } : undefined}>
            {standardItems.map(item => renderMenuItem(item, false))}
          </div>
          {customBlockItems.length > 0 && (
            <>
              <div className="agent-editor-floating-menu-title">Bloques</div>
              <div className="agent-editor-floating-menu-grid">
                {customBlockItems.map(item => renderMenuItem(item, false))}
              </div>
            </>
          )}
        </div>
      </div>
      <input type="hidden" id="breakpoints" value="[]" />

      <Modal
        isOpen={!!blockToDelete}
        type="urgent"
        title="Eliminar bloque"
        message={`¿Está seguro de que desea eliminar el bloque "${blockToDelete?.label}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDeleteBlock}
        onCancel={() => setBlockToDelete(null)}
      />
    </div>
  );
};
